import { useEffect, useState } from "react";
import style from "./expiryDates.module.scss";
import Layout from "components/layout/Layout";
import { Filter } from "components/UI/filter/filter";
import { SearchInput } from "components/UI/searchInput/searchInput";
import { ExpiryDateRow } from "components/expiryDateRow/expiryDateRow";
import { BackArrow } from "components/UI/backArrow/backArrow";
import { useHistory, useParams } from "react-router-dom";
import { RoutePaths } from "app/routing/routing";
import { createApplicationRequest, createExtensionRequest, useGetApplicationInfo, useGetApplications, useGetFileNumbers } from "actions/applicationsActions";
import { DocumentName, DocumentNames, getSMBDocument, SMBDocument, useGetDocuments, useGetDocumentTypes, useGetExpiryDocuments } from "actions/documentActions";
import NoDataMessage from "components/noDataBlock/noDataMessage";
import { fileToBase64, getFieldDocumentNames, IntStatus, isEmptyDropDownValue, parseDateString, parseFileExtensions } from "utils/commonFunctions";
import SingleFilterDropDown, { OptionItem } from "components/UI/singleFilterDropDown/singleFilterDropDown";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { LoadingContainer } from "components/UI/loadingContainer/loadingContainer";
import { Modal } from "components/modal/modal";
import Questionnaire, { QuestionnaireDataParams } from "components/questionnaire/questionnaire";
import DropDownSelect from "components/UI/dropDownSelect/dropDownSelect";
import { Button } from 'components/UI/button/button';
import DatePicker from 'components/UI/DatePicker/datePicker';
import FileUpload, { DocumentAdditionParams, DocumentDeletionParams } from 'components/UI/FileUpload/fileUpload';
import { Input } from "components/UI/input/input";
import { MultilineInput } from 'components/UI/multilineInput/multilineInput';
import { ApplicationFormFields, FileUploads, NewApplication } from "components/newApplicationForm/newApplicationForm";
import { FieldErrors, useForm } from 'react-hook-form';
import { validEmailRegex, validPhoneNumberRegex, validZipCodePostalCodeRegex } from "utils/regexValidations";
import { isValid, parse, isFuture, subYears } from 'date-fns';
import { DropdownOptions, useGetCountries } from "actions/locationsActions";
import { AllowedExtensions, formatImage } from "utils/imageFunctions";
import { getUserEmailCheck } from "actions/userActions";
import { DocumentFormFields } from "pages/documents/documents";
import useScreenWidth from "hooks/useScreenWidth";
import { ExtensionFormFields, ExtensionRequest } from "components/extensionRequestForm/extensionRequestForm";
import { ContactInfo, useGetContactInfo } from "actions/contactActions";
import confetti from 'canvas-confetti';
import confettiImage from "assets/images/confetti.png"
import translations from "locales";

const intStatusOptions = [
  { id: 'BIOMETRICS', label: 'Biometrics Deadline' },
  { id: 'ID05', label: 'Canadian Study Permit' },
  { id: 'WORKPERMIT', label: 'Canadian Work Permit' },
  { id: 'ID08', label: 'LMIA' },
  { id: 'ID09', label: 'PR Card' },
];

const sortOptions: OptionItem[] = [
  {
    label: "Expiry Date - Newest to Oldest",
    value: { orderBy: "expiry", sortOrder: "DESC" },
  },
  {
    label: "Expiry Date - Oldest to Newest",
    value: { orderBy: "expiry", sortOrder: "ASC" },
  },
  {
    label: "Document Type - Alphabetical (A to Z)",
    value: { orderBy: "type", sortOrder: "ASC" },
  },
  {
    label: "Document Type - Alphabetical (Z to A)",
    value: { orderBy: "type", sortOrder: "DESC" },
  },
];

interface UploadQueue {
  files: File[],
  success: boolean;
}

export const ExpiryDates = () => {
  const history = useHistory();
  const [searchWord, setSearchWord] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [intStatus, setIntStatus] = useState<Array<string>>([]);
  const [isFilterDropdownVisible, setFilterDropdownVisible] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState<boolean>(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QuestionnaireDataParams>();
  const [hideQuestionnaireModalHeader, setHideQuestionnaireModalHeader] = useState(false);
  const [docType, setDocType] = useState<string[]>([]);
  const [type, setType] = useState<string[]>([]);
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, setValue, getValues, trigger } = useForm<ExtensionFormFields>();
  const [provinceOptions, setProvinceOptions] = useState<DropdownOptions[]>([]);
  const [uploads, setUploads] = useState<FileUploads>({});
  const [documentNames, setDocumentNames] = useState<DocumentNames>({ localDocuments: [], uploadedDocuments: [] } as DocumentNames);
  const [dropDownSearchWord, setDropDownSearchWord] = useState<string>();
  const [uploadQueue, setUploadQueue] = useState<UploadQueue>({ files: [], success: false });
  const [selectedFileId, setSelectedFileId] = useState<string>("");
  const screenWidthForMobile = 1024;
  const screenWidth = useScreenWidth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessages, setUploadMessages] = useState<string[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [smbDocument, setSmbDocument] = useState<SMBDocument | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [clientID, setClientID] = useState<string | null>(null);
  const [contID, setContID] = useState<string | null>(null);
  const [docID, setDocID] = useState<string | null>(null);
  const [preloadedFiles, setPreloadedFiles] = useState<Partial<FileUploads> | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const lang = localStorage.getItem('lang') || 'en';


  
  const {
    data: documentTypes
  } = useGetDocumentTypes();

  const {
    data: documentData,
    error: documentDataError,
    mutate: mutateDocuments,
    noDataFound: noDocumentDataFound,
    isValidating,
    size,
    setSize,
    hasMore,
  } = useGetExpiryDocuments({ searchWord, rowNumber: 100, orderBy, sortOrder, docType, type });
  
  const getDocumentTypeLabel = (code: string): string | undefined => {
    return documentTypes?.find((dt) => dt.cdcode === code)?.cddesc;
  };

  const allowedTypes = ['WORKPERMIT'];



  const handleSortChange = (value: any) => {
    setOrderBy(value?.orderBy || "");
    setSortOrder(value?.sortOrder || "");
    resetData();

  };

  const onSearch = (value: string) => {
    setSearchWord(value);
    resetData();
  };

  const onFilterChange = async (selectedOptions: string[]) => {
    setType(selectedOptions);
    resetData();
  };

  
  useEffect(() => {
    if (showApplicationModal) {
      setIsSubmitting(false);
      setSubmissionError(null);
    }
  }, [showApplicationModal]);

  const onClickNewApplication = () => {
    setShowApplicationModal(true);
  };

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setShowApplicationModal(false);
    }
    history.push('/expiryDates');
  };

  const onSubmitApplication = async (data: ExtensionFormFields) => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      
      await createExtensionRequest({
        clientID: clientID!,
        contID: contID!,
        docID: docID!,
        formFields: data,
        reviewStatus: IntStatus.PendingReview,
        fileUploads: data.fileUploads,
      });
      setShowSuccessModal(true);
      confetti({
        particleCount: 200,
        spread: 350,
        origin: { y: 0.6 },
      });
      handleCloseModal();
      //mutateApplications();
    } catch (error: any) {
      console.error("Error submitting application: ", error);
      let errorMessage = "There was an error submitting your application. Please try again.";
      
      // Handle different types of errors
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.message && data.message.includes("Missing required parameters")) {
            errorMessage = "Your application is missing required information. Please check all fields and try again.";
          } else {
            errorMessage = data.message || errorMessage;
          }
        } else if (status === 500) {
          // Handle server errors
          errorMessage = data.message || "There was a server error. Please try again later.";
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message || errorMessage;
      }
      
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };


  const resetData = () => {
    setSize(1);
    mutateDocuments();
  };

  const loadMore = () => {
    setSize(prevSize => prevSize + 1);
  };


  const [loader] = useInfiniteScroll(loadMore, hasMore, [orderBy, sortOrder, searchWord, type]);
  

  const onClickDocument = async (doc: any) => {
    try {
      setIsLoadingPreview(true);
      setSelectedDocument(doc);
      setShowDocumentModal(true);
      const smbDoc = await getSMBDocument(doc.docFileName);
      setSmbDocument(smbDoc);
    } catch (error) {
      console.error("Failed to load document preview", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const createFileFromSMBDocument = async (doc: any): Promise<File | null> => {
    const smbDoc = await getSMBDocument(doc.docFileName);
    if (!smbDoc?.image || !smbDoc?.format) return null;
  
    let blob: Blob;
  
    if (smbDoc.image.startsWith("blob:")) {
      const response = await fetch(smbDoc.image);
      blob = await response.blob();
    } else {
      const isText = smbDoc.format.startsWith("text/") || smbDoc.format.includes("json");
  
      blob = new Blob(
        [
          isText
            ? smbDoc.image
            : new Uint8Array(
              atob(smbDoc.image.split(",")[1])
                .split("")
                .map((c) => c.charCodeAt(0))
            )
        ],
        { type: smbDoc.format }
      );
    }
  
    const fileName = doc.docFileName.split(/[/\\]/).pop() || "document";
    return new File([blob], fileName, { type: smbDoc.format });
  };

  const handleDownload = async (doc: any) => {
    try {
      setIsDownloading(true);
      const smbDoc = await getSMBDocument(doc.docFileName);
      if (!smbDoc?.image) {
        console.error("SMB document or image not found");
        return;
      }
      const url = smbDoc.image.startsWith("blob:")
        ? smbDoc.image
        : URL.createObjectURL(
          new Blob(
            [
              smbDoc.format.startsWith("text/") || smbDoc.format.includes("json")
                ? smbDoc.image
                : new Uint8Array(
                  atob(smbDoc.image.split(",")[1])
                    .split("")
                    .map((c) => c.charCodeAt(0))
                ),
            ],
            { type: smbDoc.format }
          )
        );
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.docDesc || "document";
      link.click();
      if (!smbDoc.image.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const image = formatImage(smbDocument?.format, smbDocument?.image);
    

  const { data: fileNumberOptions, error: fileNumberError } = useGetFileNumbers(dropDownSearchWord);

  const onChangeFileNumber = (value: string) => {
    setUploadQueue({ ...uploadQueue, success: false });
    setSelectedFileId(value);
    resetData();
  };

  return (
    <Layout>
      <div className={style.company}>
        <h2 className={style.headerDesktop}>
          {lang === 'fr' ? translations.fr.upcoming_expiry : translations.en.upcoming_expiry}
        </h2>
        <h2 className={style.headerMobile}>{lang === 'fr' ? translations.fr.all_matters : translations.en.all_matters}</h2>
      </div>
      <div className={style.filterButtons}>
        <Filter
          text={lang === 'fr' ? translations.fr.filter : translations.en.filter}
          isVisible={isFilterDropdownVisible}
          setVisible={setFilterDropdownVisible}
          onCheckboxChange={onFilterChange}
          filterOptions={lang === 'fr' ? translations.fr.intStatusOptionsFrExpiry: intStatusOptions}
          mobileText={lang === 'fr' ? translations.fr.filter : translations.en.filter}
          className={style.filter}
        />
        <SingleFilterDropDown
          id={'sortMatters'}
          label={lang === 'fr' ? translations.fr.sort : translations.en.sort}
          mobileText={lang === 'fr' ? translations.fr.sort : translations.en.sort}
          optionItems={lang === 'fr' ? translations.fr.sortOptionsFrExpiry : sortOptions}
          onOptionSelected={handleSortChange}
          dropDownDirectionClass={style.dropDownDirectionClass}
        />
        <SearchInput
          placeholder={lang === 'fr' ? translations.fr.try_work_permit : translations.en.try_work_permit}
          onChange={onSearch}
          className={style.searchBar}
        />
      </div>
      <div className={style.clientTableContainerWrapper}>
        <div className={style.clientTableContainer}>
          <div>
            <table className={style.clientTable}>
              <thead>
                <tr>
                  <th>{lang === 'fr' ? translations.fr.name : translations.en.name}</th>
                  <th>{lang === 'fr' ? translations.fr.document_type : translations.en.document_type}</th>
                  <th className={style.issueHeader}>{lang === 'fr' ? translations.fr.issue_date : translations.en.issue_date}</th>
                  <th className={style.expiryHeader}>{lang === 'fr' ? translations.fr.expiry_date : translations.en.expiry_date}</th>
                  <th className={style.extendButton}>{lang === 'fr' ? translations.fr.initiate_extension_matter : translations.en.initiate_extension_matter}</th>
                </tr>
              </thead>
              <tbody>
                {documentData &&
                      documentData.filter(document => document !== null)?.map((documentRowData, index) => {
                        const shouldShowButton = allowedTypes.includes(documentRowData.type?.toUpperCase());
                        const isCompleted = documentRowData.bitExpiryInitiationCompleted === true;
                        return (
                          <ExpiryDateRow
                            key={index}
                            docName={documentRowData.applicantName}
                            type={getDocumentTypeLabel(documentRowData?.type) ?? documentRowData?.type}
                            expiryDate={documentRowData.expiry}
                            issueDate={documentRowData.created}
                            //onClick={() => onClickDocument(documentRowData)}
                            onClickButton={ async () => {
                              onClickNewApplication();
                              setClientID(documentRowData.clId);
                              setContID(documentRowData.contId);
                              setDocID(documentRowData.docId);
                              history.push(`/expiryDates/contID/${documentRowData.contId}`);
                            
                              const rowFile = await createFileFromSMBDocument(documentRowData);
                            
                              const passportDoc = documentData.find(doc => 
                                doc?.type?.toUpperCase() === "ID01" && 
                                doc?.contId === documentRowData.contId
                              );

                              const resumeDoc = documentData.find(doc => 
                                doc?.type?.toUpperCase() === "ID03" && 
                                doc?.contId === documentRowData.contId
                              );
                            
                              let passportFile: File | null = null;
                              if (passportDoc) {
                                passportFile = await createFileFromSMBDocument(passportDoc);
                              }
                              
                              let resumeFile: File | null = null;
                              if (resumeDoc) {
                                resumeFile = await createFileFromSMBDocument(resumeDoc);
                              }

                              const filesToSet: { canadianUpload?: File[]; passportUpload?: File[]; resumeUpload?: File[] } = {};
                            
                              if (rowFile) {
                                filesToSet.canadianUpload = [rowFile];
                              }
                            
                              if (passportFile) {
                                filesToSet.passportUpload = [passportFile];
                              }

                              if (resumeFile) {
                                filesToSet.resumeUpload = [resumeFile];
                              }
                            
                              setPreloadedFiles(filesToSet);
                            }}
                            showButton={shouldShowButton}
                            isCompleted={isCompleted}
                          />
                        );
                      })}
                {isValidating && documentData && size > 1 && <tr><td><span>Loading...</span></td></tr>}

              </tbody>
            </table>
            <div ref={loader} style={{ height: '1px', backgroundColor: 'transparent' }} />
            {size === 1 && isValidating && <LoadingContainer loadingLabel="Loading matters..." />}
          </div>
          {noDocumentDataFound && !isValidating && (
            <NoDataMessage
              title="No Documents Found!"
              description="Please reach out to your primary contact at the firm for assistance."
            />
          )}
        </div>
        {/* <Modal
          id="reviewQuestionnaireModal"
          title={'Extension Request'}
          show={showQuestionnaireModal}
          onClose={closeQuestionnaire}
          className={style.newApplication}
          hideModalHeader={hideQuestionnaireModalHeader}
          key={`review-questionnaire-${selectedQuestionnaire?.questionnaireId}-${selectedQuestionnaire?.contId}`}
        >
          <DropDownSelect
            id="dropDown"
            title="Was the..."
            options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
            onChange={handleDropDownChange}
          ></DropDownSelect>
        </Modal> */}
        <Modal
          id="newApplicationModal"
          title="Extension Request"
          show={showApplicationModal}
          onClose={handleCloseModal}
          className={style.newApplication}
          headerClass={style.newApplicationHeader}
          hideModalHeader={isSubmitting}
        >
          {isSubmitting ? (
            <LoadingContainer loadingLabel="Your request is being submitted" />
          ) : (
            <>
              {submissionError && (
                <div style={{ 
                  color: "red", 
                  backgroundColor: "#ffeeee", 
                  padding: "10px", 
                  marginBottom: "15px", 
                  borderRadius: "4px",
                  border: "1px solid #ffcccc"
                }}>
                  {submissionError}
                </div>
              )}
              <ExtensionRequest onSubmitForm={onSubmitApplication} preloadedFiles={preloadedFiles || undefined}/>
            </>
          )}
        </Modal>
        <Modal
          id="documentPreview"
          title={selectedDocument?.docDesc || "Loading document..."}
          show={showDocumentModal}
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedDocument(null);
          }}
          className={style.documentModal}
          hideModalHeader={false}
        >
          <div className={!isLoadingPreview ? style.modalContent : style.loadingContainer}>
            {isLoadingPreview ? (
              <LoadingContainer loadingLabel="Loading document preview..." />
            ) : (
              image
            )}
          </div>
          <div className={style.modalFooter}>
            {!isLoadingPreview && (
              <Button
                id="downloadDocument"
                onClick={() => handleDownload(selectedDocument)}
                disabled={!smbDocument || isDownloading}
                style="gradient"
                className={style.downloadButton}
              >
              Download Document
              </Button>
            )}
          </div>
        </Modal>
        <Modal
          id="successModal"
          title={""}
          show={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
          }}
          className={style.successModal}
          hideModalHeader={false}
        >
          <div className={!isLoadingPreview ? style.modalContent : style.loadingContainer}>
            <img className={style.thankYouImage}
              src={confettiImage}
              alt="Celebration Confetti"
            />
            <h2 className={style.thankYouTitle}>Thank you!</h2>
            <p className={style.thankYouMessage}>
              Your submission has been received. <br />
              We will be in touch if further information is required.
            </p>
          </div>
          <div className={style.modalFooter}>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};